// Build chart options from the live CSS variables so charts inherit the
// active Bootstrap theme. Read at chart-creation time and again whenever
// theme_controller dispatches `theme:changed`.

export function cssVar(name, fallback) {
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return value || fallback
}

// Colours used by lightweight-charts (TradingView). Same field names as the
// old CHART_COLORS constant so existing call sites just pick this up.
export function getChartColors() {
  return {
    up: '#26a69a',                                  // candle/volume green — unchanged
    down: '#ef5350',                                // candle/volume red   — unchanged
    background: cssVar('--bs-body-bg', '#ffffff'),
    text: cssVar('--bs-body-color', '#333333'),
    grid: cssVar('--bs-border-color', '#f0f3fa'),
    border: cssVar('--bs-border-color', '#D1D4DC'),
    crosshair: cssVar('--bs-secondary', '#758696'),
    watermark: cssVar('--bs-body-color', '#000000')
  }
}

// Global option overrides for Highcharts so axes/labels/tooltips/grid all
// follow the active theme. Re-applied via Highcharts.setOptions on boot and
// on every theme:changed event.
export function getHighchartsTheme() {
  const text = cssVar('--bs-body-color', '#212529')
  const muted = cssVar('--bs-secondary-color', '#6c757d')
  const grid = cssVar('--bs-border-color', '#dee2e6')
  const bg = cssVar('--bs-body-bg', '#ffffff')

  return {
    chart: {
      backgroundColor: 'transparent',
      style: { color: text }
    },
    title: { style: { color: text } },
    subtitle: { style: { color: muted } },
    xAxis: {
      lineColor: grid,
      tickColor: grid,
      gridLineColor: grid,
      labels: { style: { color: muted } },
      title: { style: { color: muted } }
    },
    yAxis: {
      lineColor: grid,
      tickColor: grid,
      gridLineColor: grid,
      labels: { style: { color: muted } },
      title: { style: { color: muted } }
    },
    legend: {
      itemStyle: { color: text },
      itemHoverStyle: { color: text },
      itemHiddenStyle: { color: muted }
    },
    tooltip: {
      backgroundColor: bg,
      borderColor: grid,
      style: { color: text }
    },
    plotOptions: {
      series: { dataLabels: { color: text } }
    },
    navigator: {
      maskFill: 'rgba(102, 102, 102, 0.10)',
      series: { color: muted },
      xAxis: { gridLineColor: grid, labels: { style: { color: muted } } }
    }
  }
}

// Re-resolve per-chart colours when the user toggles light/dark. The global
// theme handler (application.js) only re-applies axis text/grid colours via
// getHighchartsTheme(); per-chart series and plot band colours are re-applied
// through the `apply` callback here. Subscribes to theme:changed and cleans
// itself up when the chart is destroyed.
export function watchChartTheme(chart, apply) {
  const onThemeChanged = () => {
    if (!chart || !chart.renderer) return
    try {
      apply(chart)
    } catch (e) {
      console.warn('Failed to re-apply chart theme:', e)
    }
  }
  document.addEventListener('theme:changed', onThemeChanged)
  Highcharts.addEvent(chart, 'destroy', () => {
    document.removeEventListener('theme:changed', onThemeChanged)
  })
}
